import { Dispatch } from 'react';

import { Annotation, Coordinates, DrawingTypes, PageAnnotations, PageItems } from '@/types';

import { AppState } from '@/context/appContext';
import { DrawingContext } from '@/functions/canvasFunctions';
import { canvasCoordinatesToPdfCoordinates } from '@/helpers';

export enum AnnotationActionType {
  SET_ANNOTATION_PAGES = 'SET_ANNOTATION_PAGES',
  ADD = 'ADD_ANNOTATION',

  COLOR = 'COLOR_ANNOTATION',
  WIDTH = 'WIDTH_ANNOTATION',
  UPDATE = 'UPDATE_ANNOTATION',

  DELETE = 'DELETE_ANNOTATION',
}

export type AnnotationEvent =
  | {
      type: AnnotationActionType.SET_ANNOTATION_PAGES;
      payload: { pageAnnotations: PageAnnotations };
    }
  | {
      type: AnnotationActionType.ADD;
      payload: {
        id: string;
        url: string;
        pageItems: PageItems;
        scale: number;
        imageMeta: { width: number; height: number };
        position: Coordinates;
        pageNumber: number;
        drawingContext: DrawingContext;
        color: string;
        lineWidth?: number;
        newAnnotationType: DrawingTypes;
      };
    }
  | {
      type: AnnotationActionType.DELETE;
      payload: { id: string };
    }
  | {
      type: AnnotationActionType.COLOR | AnnotationActionType.WIDTH | AnnotationActionType.UPDATE;
      payload: Partial<Omit<Annotation, 'id'>> & { id: Annotation['id'] };
    };

const AnnotationsReducer = (appState: AppState, action: AnnotationEvent): AppState => {
  const { annotations: pageAnnotations } = appState;

  switch (action.type) {
    case AnnotationActionType.SET_ANNOTATION_PAGES: {
      return { ...appState, annotations: { ...action.payload.pageAnnotations } };
    }
    case AnnotationActionType.ADD: {
      const {
        id,
        url,
        pageItems,
        scale,
        imageMeta: { height, width },
        position: { x, y },
        pageNumber,
        drawingContext,
        color,
        lineWidth,
        newAnnotationType,
      } = action.payload;

      const newAnnotation: Annotation = {
        id,
        url,
        currentCanvasCoordinates: { x, y, width, height },
        currentPdfCoordinates: canvasCoordinatesToPdfCoordinates(
          { x, y: y + height, width, height },
          pageItems[pageNumber].page,
          scale,
        ),
        paths: [...drawingContext.paths],
        type: newAnnotationType,
        color,
        lineWidth,
        scale,
      };

      const newItems = { ...pageAnnotations };
      if (newItems[pageNumber]) {
        newItems[pageNumber] = [...newItems[pageNumber], newAnnotation];
      } else {
        newItems[pageNumber] = [newAnnotation];
      }

      return { ...appState, annotations: newItems };
    }
    case AnnotationActionType.DELETE: {
      const newItems: PageAnnotations = {};
      const urlsToRevoke: string[] = [];

      // TODO: add page number in payload
      Object.keys(pageAnnotations).forEach((pageNumber) => {
        const newImageItems = pageAnnotations[pageNumber].filter((imageItem) => {
          if (imageItem.id === action.payload.id) {
            urlsToRevoke.push(imageItem.url);
            return false;
          }

          return true;
        });
        newItems[pageNumber] = newImageItems;
      });

      return {
        ...appState,
        annotations: newItems,
        urlsToRevokePerPastState: [...appState.urlsToRevokePerPastState, urlsToRevoke],
      };
    }
    case AnnotationActionType.COLOR:
    case AnnotationActionType.WIDTH:
    case AnnotationActionType.UPDATE: {
      const newItems: PageAnnotations = { ...pageAnnotations };

      Object.keys(pageAnnotations).forEach((pageNumber) => {
        newItems[pageNumber] = pageAnnotations[pageNumber].map((imageItem) => {
          if (imageItem.id === action.payload.id) {
            return { ...imageItem, ...action.payload };
          }

          return imageItem;
        });
      });

      return { ...appState, annotations: newItems };
    }
    default:
      return appState;
  }
};

const findAnnotationById = (annotationId: string, annotations: Annotation[]) => {
  return annotations.find((annotation) => annotation.id === annotationId);
};

export const changeAnnotationColor = (
  annotationId: string,
  color: string,
  state: Annotation[],
  dispatch: Dispatch<AnnotationEvent>,
) => {
  const annotation = findAnnotationById(annotationId, state);
  if (!annotation) return;
  dispatch({ type: AnnotationActionType.UPDATE, payload: { id: annotation.id, color } });
};

export const changeAnnotationWidth = (
  annotationId: string,
  newWidth: number,
  state: Annotation[],
  dispatch: Dispatch<AnnotationEvent>,
) => {
  const annotation = findAnnotationById(annotationId, state);
  if (!annotation) return;
  dispatch({ type: AnnotationActionType.UPDATE, payload: { id: annotation.id, lineWidth: newWidth } });
};

export default AnnotationsReducer;
